import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import {
  client,
  type SiteOfCare,
  type SitesOfCareResponse,
  type SiteOfCareRequest,
  type BenefitPreference,
  type BenefitPreferencesResponse,
  type BenefitPreferenceRequest,
  type MedicalBenefitDrugsResponse,
} from '../api';

const SITES_OF_CARE_QUERY_KEY = ['benefit-preferences', 'sites'];
const BENEFIT_PREFERENCES_QUERY_KEY = ['benefit-preferences', 'preferences'];
const MEDICAL_BENEFIT_DRUGS_QUERY_KEY = ['benefit-preferences', 'drugs'];

export type ApiErrorDetail = {
  code?: string;
  message?: string;
};

export type ApiError = AxiosError<{ detail: ApiErrorDetail | string }>;

/**
 * Hook to fetch sites of care for the current practice
 */
export const useSitesOfCare = () => {
  return useQuery<SitesOfCareResponse>({
    queryKey: SITES_OF_CARE_QUERY_KEY,
    queryFn: async () => {
      const response = await client.get<SitesOfCareResponse>('/settings/benefit-preferences/sites');
      return response.data;
    },
  });
};

interface UseMutationConfig<E = Error> {
  onSuccess?: () => void;
  onError?: (error: E) => void;
}

/**
 * Hook to create a new site of care
 */
export const useCreateSiteOfCare = (
  config: { onSuccess?: (site: SiteOfCare) => void; onError?: (error: Error) => void } = {},
) => {
  const queryClient = useQueryClient();

  return useMutation<SiteOfCare, Error, SiteOfCareRequest>({
    mutationFn: async (data) => {
      const response = await client.post<SiteOfCare>('/settings/benefit-preferences/sites', data);
      return response.data;
    },
    onSuccess: (site) => {
      queryClient.invalidateQueries({ queryKey: SITES_OF_CARE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BENEFIT_PREFERENCES_QUERY_KEY });
      config.onSuccess?.(site);
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};

/**
 * Hook to delete a site of care
 */
export const useDeleteSiteOfCare = (config: UseMutationConfig = {}) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (siteId) => {
      await client.delete(`/settings/benefit-preferences/sites/${siteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SITES_OF_CARE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BENEFIT_PREFERENCES_QUERY_KEY });
      config.onSuccess?.();
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};

/**
 * Hook to fetch benefit preferences for the current practice
 */
export const useBenefitPreferences = () => {
  return useQuery<BenefitPreferencesResponse>({
    queryKey: BENEFIT_PREFERENCES_QUERY_KEY,
    queryFn: async () => {
      const response = await client.get<BenefitPreferencesResponse>('/settings/benefit-preferences/preferences');
      return response.data;
    },
  });
};

/**
 * Hook to create a new benefit preference
 */
export const useCreateBenefitPreference = (config: UseMutationConfig<ApiError> = {}) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, ApiError, BenefitPreferenceRequest>({
    mutationFn: async (data) => {
      const response = await client.post<BenefitPreference>('/settings/benefit-preferences/preferences', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BENEFIT_PREFERENCES_QUERY_KEY });
      config.onSuccess?.();
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};

/**
 * Hook to update existing benefit preference
 */
export const useUpdateBenefitPreference = (config: UseMutationConfig<ApiError> = {}) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, ApiError, { preferenceId: string; data: BenefitPreferenceRequest }>({
    mutationFn: async ({ preferenceId, data }) => {
      const response = await client.put<BenefitPreference>(
        `/settings/benefit-preferences/preferences/${preferenceId}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BENEFIT_PREFERENCES_QUERY_KEY });
      config.onSuccess?.();
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};

/**
 * Hook to delete benefit preference
 */
export const useDeleteBenefitPreference = (config: UseMutationConfig = {}) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (preferenceId) => {
      await client.delete(`/settings/benefit-preferences/preferences/${preferenceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BENEFIT_PREFERENCES_QUERY_KEY });
      config.onSuccess?.();
    },
    onError: (error) => {
      Sentry.captureException(error);
      config.onError?.(error);
    },
  });
};

/**
 * Hook to fetch drugs eligible for medical benefits
 */
export const useMedicalBenefitDrugs = () => {
  return useQuery<MedicalBenefitDrugsResponse>({
    queryKey: MEDICAL_BENEFIT_DRUGS_QUERY_KEY,
    queryFn: async () => {
      const response = await client.get<MedicalBenefitDrugsResponse>('/settings/benefit-preferences/drugs');
      return response.data;
    },
  });
};
